<script setup lang="ts" generic="T">
import { computed, ref } from 'vue';
import draggable from 'vuedraggable';

const DRAG_ANIMATION_MS = 150;
const TOUCH_DELAY_MS = 200;

const props = defineProps<{
  modelValue: T[];
  itemKey: string;
  divider?: boolean;
  disabled?: boolean;
  groupName?: string;
  handle?: string;
}>();

const emits = defineEmits<{
  (
    e: 'moved',
    payload: { oldIndex: number; newIndex: number; element: T },
  ): void;
  (e: 'drag-start', element: T): void;
  (e: 'drag-end'): void;
  (e: 'update:modelValue', value: T[]): void;
}>();

defineSlots<{
  item(props: { item: T; index: number }): unknown;
}>();

const dragging = ref(false);

// Compute internal list to respect Vue's one-way data flow (v-model)
const internalList = computed({
  get: () => props.modelValue,
  set: (value) => emits('update:modelValue', value),
});

const dragOptions = computed(() => ({
  animation: DRAG_ANIMATION_MS,
  group: props.groupName ?? 'description',
  disabled: props.disabled,
  handle: props.handle,
  ghostClass: 'ghost',
  chosenClass: 'sortable-chosen',
}));

// Generate a key for each item
const getItemKey = (item: T, index: number): string | number => {
  if (!props.itemKey) {
    return index;
  }
  // eslint-disable-next-line @typescript-eslint/no-unsafe-return, @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-member-access
  return (item as any)[props.itemKey] ?? index;
};
type DraggableChangeEvent<TItem> = {
  moved?: {
    oldIndex: number;
    newIndex: number;
    element: TItem;
  };
};

type DraggableStartEvent = {
  oldIndex?: number;
};

const handleStart = (evt: DraggableStartEvent): void => {
  dragging.value = true;

  if (typeof evt.oldIndex !== 'number') {
    return;
  }

  const element = internalList.value[evt.oldIndex];
  if (element !== undefined) {
    emits('drag-start', element);
  }
};

const handleEnd = (): void => {
  dragging.value = false;
  emits('drag-end');
};

const handleChange = (evt: DraggableChangeEvent<T>): void => {
  if (evt.moved) {
    emits('moved', {
      oldIndex: evt.moved.oldIndex,
      newIndex: evt.moved.newIndex,
      element: evt.moved.element,
    });
  }
};
</script>

<template>
  <draggable
    v-model="internalList"
    class="list-group divide-y divide-neutral-200"
    tag="div"
    v-bind="dragOptions"
    :item-key="getItemKey"
    :delay="TOUCH_DELAY_MS"
    :delay-on-touch-only="true"
    @start="handleStart"
    @end="handleEnd"
    @change="handleChange"
  >
    <template #item="{ element, index }">
      <div>
        <slot name="item" :item="element" :index="index">
          {{ element }}
        </slot>
      </div>
    </template>
  </draggable>
</template>

<style scoped>
.button {
  margin-top: 35px;
}

.flip-list-move {
  transition: transform 0.5s;
}

.no-move {
  transition: transform 0s;
}

.ghost {
  opacity: 0.5;
}

.list-group {
  min-height: 20px;
  /* Prevent text selection during the touch-hold delay on mobile */
  -webkit-user-select: none;
  user-select: none;
}

.list-group-item {
  cursor: move;
}

.list-group-item i {
  cursor: pointer;
}

.sortable-chosen {
  border: none !important;
  outline: none !important;
}
</style>
<!--.sortable-chosen { border: none !important; outline: none !important; }-->
